import React, { useState } from 'react';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const T = {
  az: {
    title: 'Səbət',
    empty: 'Səbətiniz boşdur',
    emptyHint: 'Menyudan məhsul əlavə edin',
    table: 'Masa nömrəsi',
    tableHint: 'Hansı masadasınız?',
    name: 'Ad (istəyə bağlı)',
    phone: 'Telefon (istəyə bağlı)',
    notes: 'Qeyd (istəyə bağlı)',
    itemNotes: 'Qeyd',
    subtotal: 'Ara cəm',
    serviceFee: 'Xidmət haqqı',
    total: 'Ümumi məbləğ',
    place: 'Sifariş ver',
    placing: 'Göndərilir...',
    success: 'Sifarişiniz qəbul edildi!',
    callWaiter: 'Ofisiantı çağır',
    requestBill: 'Hesab istə',
    selectTable: 'Masa seçin',
    minTable: 'Düzgün masa nömrəsi daxil edin',
    error: 'Xəta',
  },
  en: {
    title: 'Cart',
    empty: 'Your cart is empty',
    emptyHint: 'Add items from the menu',
    table: 'Table number',
    tableHint: 'Which table are you at?',
    name: 'Name (optional)',
    phone: 'Phone (optional)',
    notes: 'Notes (optional)',
    itemNotes: 'Note',
    subtotal: 'Subtotal',
    serviceFee: 'Service fee',
    total: 'Total',
    place: 'Place order',
    placing: 'Placing...',
    success: 'Order received!',
    callWaiter: 'Call waiter',
    requestBill: 'Request bill',
    selectTable: 'Select table',
    minTable: 'Enter a valid table number',
    error: 'Error',
  },
  ru: {
    title: 'Корзина',
    empty: 'Ваша корзина пуста',
    emptyHint: 'Добавьте товары из меню',
    table: 'Номер стола',
    tableHint: 'За каким столом вы?',
    name: 'Имя (необязательно)',
    phone: 'Телефон (необязательно)',
    notes: 'Заметка (необязательно)',
    itemNotes: 'Заметка',
    subtotal: 'Подытог',
    serviceFee: 'Сервисный сбор',
    total: 'Итого',
    place: 'Оформить заказ',
    placing: 'Отправка...',
    success: 'Заказ принят!',
    callWaiter: 'Позвать официанта',
    requestBill: 'Попросить счёт',
    selectTable: 'Выберите стол',
    minTable: 'Введите корректный номер стола',
    error: 'Ошибка',
  },
};

const getLabel = (it, lang) => it[`name_${lang}`] || it.name_az || '';

export const CartDrawer = ({ restaurant, lang = 'az' }) => {
  const cart = useCart();
  const t = T[lang] || T.az;
  const [tableInput, setTableInput] = useState(cart.tableNumber || '');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [placing, setPlacing] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [callBusy, setCallBusy] = useState(false);
  const [callMsg, setCallMsg] = useState('');
  const [policy, setPolicy] = useState(null);

  const symbol = restaurant?.currency_symbol || '₼';
  const serviceFeePct = parseFloat(restaurant?.service_fee_percentage || 0);
  const serviceFee = +(cart.subtotal * serviceFeePct / 100).toFixed(2);
  const total = +(cart.subtotal + serviceFee).toFixed(2);

  // Fetch security policy when drawer opens to know if GPS is needed
  React.useEffect(() => {
    if (cart.isOpen && restaurant?.id && !policy) {
      axios
        .get(`${BACKEND_URL}/api/restaurants/${restaurant.id}/security-policy`)
        .then((r) => setPolicy(r.data))
        .catch(() => setPolicy({}));
    }
  }, [cart.isOpen, restaurant?.id, policy]);

  const getGeo = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 7000, maximumAge: 30000 }
      );
    });
  };

  const handlePlaceOrder = async () => {
    setError('');
    const tableNum = cart.isTableLocked ? cart.tableNumber : parseInt(tableInput, 10);
    if (!tableNum || tableNum < 1 || tableNum > 200) {
      setError(t.minTable);
      return;
    }
    if (cart.items.length === 0) return;

    setPlacing(true);
    try {
      // If geo required, fetch location first
      const geo = policy?.geo_enabled ? await getGeo() : null;
      const payload = {
        restaurant_id: restaurant.id,
        table_number: tableNum,
        items: cart.items.map((i) => ({
          item_id: i.item_id,
          name_az: i.name_az,
          name_en: i.name_en,
          name_ru: i.name_ru,
          price: i.price,
          quantity: i.quantity,
          notes: i.notes || null,
        })),
        customer_name: customerName.trim() || null,
        customer_phone: customerPhone.trim() || null,
        notes: orderNotes.trim() || null,
        customer_geo: geo,
      };
      const { data } = await axios.post(`${BACKEND_URL}/api/orders`, payload);
      cart.setTableNumber(tableNum);
      setSuccess(data);
      cart.clear();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        setError(detail.message);
      } else {
        setError(typeof detail === 'string' ? detail : 'Xəta baş verdi');
      }
    } finally {
      setPlacing(false);
    }
  };

  const callWaiter = async (type) => {
    const tableNum = cart.isTableLocked ? cart.tableNumber : parseInt(tableInput, 10);
    if (!tableNum || tableNum < 1 || tableNum > 200) {
      setError(t.minTable);
      return;
    }
    setCallBusy(true);
    setCallMsg('');
    setError('');
    try {
      const geo = policy?.geo_enabled ? await getGeo() : null;
      const { data } = await axios.post(`${BACKEND_URL}/api/waiter-calls`, {
        restaurant_id: restaurant.id,
        table_number: tableNum,
        type,
        customer_geo: geo,
      });
      cart.setTableNumber(tableNum);
      setCallMsg(data.message || (type === 'call_waiter' ? '🛎️ Ofisiant çağırıldı' : '🧾 Hesab tələb olundu'));
      setTimeout(() => setCallMsg(''), 4000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        setError(detail.message);
      } else {
        setError(typeof detail === 'string' ? detail : 'Xəta baş verdi');
      }
    } finally {
      setCallBusy(false);
    }
  };

  if (!cart.isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="cart-drawer">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => cart.setIsOpen(false)}
      />
      <div className="relative ml-auto w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between bg-stone-900 text-white">
          <h2 className="text-xl font-bold">🛒 {t.title}</h2>
          <button
            onClick={() => cart.setIsOpen(false)}
            className="text-2xl hover:opacity-70"
            data-testid="cart-close"
          >×</button>
        </div>

        {/* Success state */}
        {success ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center">
              <div className="text-6xl mb-3">{success.status === 'awaiting_confirmation' ? '⏳' : '✅'}</div>
              <h3 className="text-2xl font-bold text-stone-900 mb-2">
                {success.status === 'awaiting_confirmation' ? 'Sifariş təsdiq gözləyir' : t.success}
              </h3>
              <p className="text-stone-500 mb-4">Sifariş #: <strong>{success.order_number}</strong></p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-800">
                Masa {success.table_number} • {symbol}{success.total}
              </div>
              {success.status === 'awaiting_confirmation' && (
                <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                  ℹ️ Ofisiantımız təsdiq etdikdən sonra sifariş mətbəxə göndəriləcək.
                </p>
              )}
              <button
                onClick={() => { setSuccess(null); cart.setIsOpen(false); }}
                className="mt-5 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold"
                data-testid="cart-success-close"
              >
                Menyuya qayıt
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Items list */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {cart.items.length === 0 ? (
                <div className="text-center py-10">
                  <div className="text-5xl mb-3">🛒</div>
                  <p className="text-stone-600 font-semibold">{t.empty}</p>
                  <p className="text-stone-400 text-sm mt-1">{t.emptyHint}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.items.map((it) => (
                    <div key={it.item_id} className="border border-stone-200 rounded-xl p-3 bg-stone-50">
                      <div className="flex gap-3">
                        {it.image_url && (
                          <img src={it.image_url} alt="" className="w-14 h-14 rounded-lg object-cover" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-stone-900 text-sm truncate">
                            {getLabel(it, lang)}
                          </p>
                          <p className="text-xs text-stone-500">{symbol}{it.price.toFixed(2)}</p>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => cart.updateQuantity(it.item_id, -1)}
                                className="w-7 h-7 rounded-full bg-stone-200 hover:bg-stone-300 font-bold"
                                data-testid={`cart-decrease-${it.item_id}`}
                              >−</button>
                              <span className="font-bold w-6 text-center" data-testid={`cart-qty-${it.item_id}`}>{it.quantity}</span>
                              <button
                                onClick={() => cart.updateQuantity(it.item_id, 1)}
                                className="w-7 h-7 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 font-bold"
                                data-testid={`cart-increase-${it.item_id}`}
                              >+</button>
                            </div>
                            <p className="font-bold text-emerald-700">{symbol}{(it.price * it.quantity).toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Customer info + table */}
              <div className="mt-5 space-y-3">
                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase mb-1">🪑 {t.table} *</label>
                  {cart.isTableLocked ? (
                    <div
                      className="w-full px-4 py-3 bg-emerald-50 border-2 border-emerald-300 rounded-xl flex items-center justify-between"
                      data-testid="cart-table-locked"
                    >
                      <span className="font-bold text-emerald-700">Masa #{cart.tableNumber}</span>
                      <span className="text-[10px] uppercase font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full">QR-dən</span>
                    </div>
                  ) : (
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={tableInput}
                      onChange={(e) => setTableInput(e.target.value)}
                      placeholder={t.tableHint}
                      className="w-full px-4 py-2.5 border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                      data-testid="cart-table-input"
                    />
                  )}
                </div>
                {cart.items.length > 0 && (
                  <>
                    <div>
                      <label className="block text-xs font-bold text-stone-700 uppercase mb-1">{t.name}</label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full px-4 py-2 border border-stone-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500"
                        data-testid="cart-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-stone-700 uppercase mb-1">{t.notes}</label>
                      <textarea
                        rows="2"
                        value={orderNotes}
                        onChange={(e) => setOrderNotes(e.target.value)}
                        className="w-full px-4 py-2 border border-stone-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500"
                        data-testid="cart-notes-input"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Waiter call buttons (always visible) */}
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  onClick={() => callWaiter('call_waiter')}
                  disabled={callBusy}
                  className="px-3 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold text-sm disabled:opacity-50"
                  data-testid="call-waiter-btn"
                >
                  🛎️ {t.callWaiter}
                </button>
                <button
                  onClick={() => callWaiter('request_bill')}
                  disabled={callBusy}
                  className="px-3 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50"
                  data-testid="request-bill-btn"
                >
                  🧾 {t.requestBill}
                </button>
              </div>
              {callMsg && (
                <p className="mt-2 text-center text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2" data-testid="call-msg">
                  {callMsg}
                </p>
              )}
              {error && (
                <p className="mt-2 text-center text-sm font-medium text-red-700 bg-red-50 rounded-lg px-3 py-2" data-testid="cart-error">
                  ⚠️ {error}
                </p>
              )}
            </div>

            {/* Footer total + place order */}
            {cart.items.length > 0 && (
              <div className="border-t border-stone-200 bg-stone-50 px-5 py-4">
                <div className="space-y-1 text-sm mb-3">
                  <div className="flex justify-between">
                    <span className="text-stone-600">{t.subtotal}</span>
                    <span className="font-semibold">{symbol}{cart.subtotal.toFixed(2)}</span>
                  </div>
                  {serviceFeePct > 0 && (
                    <div className="flex justify-between">
                      <span className="text-stone-600">{t.serviceFee} ({serviceFeePct}%)</span>
                      <span className="font-semibold">{symbol}{serviceFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-1 border-t border-stone-200">
                    <span>{t.total}</span>
                    <span className="text-emerald-700" data-testid="cart-total">{symbol}{total.toFixed(2)}</span>
                  </div>
                </div>
                <button
                  onClick={handlePlaceOrder}
                  disabled={placing || cart.items.length === 0}
                  className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white rounded-xl font-bold transition shadow-lg shadow-emerald-500/30"
                  data-testid="cart-place-order-btn"
                >
                  {placing ? t.placing : `${t.place} • ${symbol}${total.toFixed(2)}`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const CartFab = ({ lang = 'az' }) => {
  const cart = useCart();
  if (cart.totalQuantity === 0) return null;
  return (
    <button
      onClick={() => cart.setIsOpen(true)}
      className="fixed bottom-5 right-5 z-[60] bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-3.5 rounded-full shadow-2xl shadow-emerald-500/40 flex items-center gap-2 font-bold transition hover:scale-105"
      data-testid="cart-fab"
    >
      🛒 <span data-testid="cart-fab-count">{cart.totalQuantity}</span>
      <span className="text-sm">•</span>
      <span className="text-sm">₼{cart.subtotal.toFixed(2)}</span>
    </button>
  );
};
